import { defineStore } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { Countries, GenericObject } from '@/types';
import RequestsUtils from '@/helpers/RequestsUtils';
import { AxiosError, AxiosResponse } from 'axios';
import { ref } from 'vue';
import _ from 'lodash';

export const useDatasetsStore = defineStore('datasets', () => {
  const branches = useBranchesStore();

  // Data
  const _asnsList = ref([]);
  const _asnsSortedList = ref([]);
  const _asnsLoading = ref(false);
  const _asnsListPromiseResolve = ref(null);
  const _asnsListPromiseReject = ref(null);
  const _asnsListPromise = ref(null);
  const _countriesList = ref({});
  const _countriesSortedList = ref([]);
  const _countriesLoading = ref(false);
  const _countriesListPromiseResolve = ref(null);
  const _countriesListPromiseReject = ref(null);
  const _countriesListPromise = ref(null);

  // Methods
  function getAsns(): Promise<GenericObject[]> {
    if (_asnsLoading.value) {
      return _asnsListPromise.value;
    } else if (_asnsListPromise.value) {
      return new Promise((resolve) => {
        resolve(_asnsList.value);
      });
    } else {
      return loadAsns();
    }
  }

  async function getAsnsSortedList(): Promise<[string, string][]> {
    await getAsns();
    return _asnsSortedList.value;
  }

  async function loadAsns() {
    if (!branches.selectedBranchId) {
      await branches.loadBranches();
    }
    if (_asnsLoading.value) {
      return _asnsListPromise.value;
    }
    _asnsLoading.value = true;
    _asnsListPromise.value = new Promise((resolve, reject) => {
      _asnsListPromiseResolve.value = resolve;
      _asnsListPromiseReject.value = reject;
    }) as Promise<GenericObject[]>;
    RequestsUtils.sendRequest({
      methodName: 'GET',
      url: `conf/${branches.selectedBranchId}/blobs/asns`,
    })
      .then((response: AxiosResponse) => {
        if (response?.data) {
          _asnsList.value = response.data?.blob;
          _asnsSortedList.value = _.sortBy(
            _.map(_asnsList.value, (item, key) => {
              return [key, `AS${key} ${item}`];
            }),
            (dropdownItem) => {
              return dropdownItem[1];
            }
          );
        }
        _asnsLoading.value = false;
        _asnsListPromiseResolve.value(_asnsList.value);
      })
      .catch((err: AxiosError) => {
        console.log('Error while attempting to load ASNs');
        console.log(err);
        _asnsLoading.value = false;
        _asnsListPromiseReject.value(err);
      });
    return _asnsListPromise.value;
  }

  function getCountries(): Promise<Countries> {
    if (_countriesLoading.value) {
      return _countriesListPromise.value;
    } else if (_countriesListPromise.value) {
      return new Promise((resolve) => {
        resolve(_countriesList.value);
      });
    } else {
      return loadCountries();
    }
  }

  async function getCountriesSortedList(): Promise<[string, string][]> {
    await getCountries();
    return _countriesSortedList.value;
  }

  async function loadCountries() {
    if (!branches.selectedBranchId) {
      await branches.loadBranches();
    }
    if (_countriesLoading.value) {
      return _countriesListPromise.value;
    }
    _countriesLoading.value = true;
    _countriesListPromise.value = new Promise((resolve, reject) => {
      _countriesListPromiseResolve.value = resolve;
      _countriesListPromiseReject.value = reject;
    }) as Promise<Countries>;
    RequestsUtils.sendRequest({
      methodName: 'GET',
      url: `conf/${branches.selectedBranchId}/blobs/countries`,
    })
      .then((response: AxiosResponse) => {
        if (response?.data) {
          _countriesList.value = response.data?.blob;
          _countriesSortedList.value = _.sortBy(
            _.map(_countriesList.value, (item) => {
              return [item, item];
            }),
            (dropdownItem) => {
              return dropdownItem[1];
            }
          );
        }
        _countriesLoading.value = false;
        _countriesListPromiseResolve.value(_countriesList.value);
      })
      .catch((err: AxiosError) => {
        console.log('Error while attempting to load countries');
        console.log(err);
        _countriesLoading.value = false;
        _countriesListPromiseReject.value(err);
      });
    return _countriesListPromise.value;
  }

  return {
    getAsns,
    getAsnsSortedList,
    loadAsns,
    getCountries,
    getCountriesSortedList,
    loadCountries,
  };
});
